import { lazy, Suspense, useEffect, useRef } from "react";
import { Switch, Route, Router as WouterRouter, Redirect } from "wouter";
import { QueryClient, QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { setAuthTokenGetter } from "@workspace/api-client-react";
import { AuthProvider, useAuth } from "@/lib/auth";
import { PinLock } from "@/components/pin-lock";
import { CookieConsent } from "@/components/cookie-consent";
import { UploadProvider } from "@/hooks/use-upload-progress";

const Dashboard     = lazy(() => import("@/pages/dashboard"));
const MyDrive       = lazy(() => import("@/pages/my-drive"));
const Starred       = lazy(() => import("@/pages/starred"));
const Recent        = lazy(() => import("@/pages/recent"));
const Trash         = lazy(() => import("@/pages/trash"));
const SearchResults = lazy(() => import("@/pages/search"));
const SharedFile    = lazy(() => import("@/pages/share"));
const Landing       = lazy(() => import("@/pages/landing"));
const Manifest      = lazy(() => import("@/pages/manifest"));
const Privacy       = lazy(() => import("@/pages/privacy"));
const Terms         = lazy(() => import("@/pages/terms"));
const Offer         = lazy(() => import("@/pages/offer"));
const Refund        = lazy(() => import("@/pages/refund"));
const PlansPage     = lazy(() => import("@/pages/plans"));
const Releases      = lazy(() => import("@/pages/releases"));
const NotFound      = lazy(() => import("@/pages/not-found"));
const AdminLogin    = lazy(() => import("@/pages/admin-login"));
const AdminPanel    = lazy(() => import("@/pages/admin"));
const SignInPage    = lazy(() => import("@/pages/sign-in"));
const SignUpPage    = lazy(() => import("@/pages/sign-up"));
const GiftStorage      = lazy(() => import("@/pages/gift-storage"));
const SettingsPage     = lazy(() => import("@/pages/settings"));
const MigrationPage    = lazy(() => import("@/pages/migration"));
const PaymentSuccess   = lazy(() => import("@/pages/payment-success"));
const PaymentFail      = lazy(() => import("@/pages/payment-fail"));
const HelpPage         = lazy(() => import("@/pages/help"));
const FaqPage          = lazy(() => import("@/pages/faq"));
const DuplicatesPage   = lazy(() => import("@/pages/duplicates"));
const OAuthAuthorize   = lazy(() => import("@/pages/oauth-authorize"));
const DevPortal        = lazy(() => import("@/pages/dev"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60_000,
      retry: 1,
    },
  },
});

const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

function PageLoader() {
  return (
    <div style={{
      minHeight: "100vh",
      background: "#02050A",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}>
      <div style={{
        width: 40, height: 40,
        border: "3px solid rgba(0,240,255,0.15)",
        borderTopColor: "#00F0FF",
        borderRadius: "50%",
        animation: "spin 0.7s linear infinite",
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function useRefTracking() {
  useEffect(() => {
    try {
      const ref = new URLSearchParams(window.location.search).get("ref");
      if (ref) localStorage.setItem("potop_ref", ref);
    } catch {}
  }, []);
}

function HomeRedirect() {
  useRefTracking();
  const { isLoaded } = useAuth();

  if (!isLoaded) return <PageLoader />;

  return (
    <Suspense fallback={<PageLoader />}>
      <Landing />
    </Suspense>
  );
}

function ProtectedRoute({ component: Component }: { component: React.ComponentType }) {
  const { isLoaded, isSignedIn, isLocked, user, unlockPin, logout, trustDevice } = useAuth();

  if (!isLoaded) return <PageLoader />;

  if (!isSignedIn) return <Redirect to="/sign-in" />;

  if (isLocked && user) {
    return (
      <PinLock
        userName={user.name || user.email}
        onUnlock={() => {
          unlockPin();
        }}
        onLogout={logout}
        onForgot={() => {
          logout();
          window.location.href = "/sign-in";
        }}
      />
    );
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Component />
    </Suspense>
  );
}

function TokenBridge() {
  const { getToken, user } = useAuth();
  const prevUserIdRef = useRef<string | null>(null);
  const qc = useQueryClient();

  useEffect(() => {
    setAuthTokenGetter(() => getToken());
    return () => { setAuthTokenGetter(null); };
  }, [getToken]);

  // Clear the cache only when the active account actually changes from one
  // real user to a different real user. A plain token refresh (or logout to
  // the signed-out state) keeps the same userId, so the cache is preserved.
  useEffect(() => {
    const userId = user?.id ?? null;
    if (userId === null) return;
    if (prevUserIdRef.current !== null && prevUserIdRef.current !== userId) {
      qc.clear();
    }
    prevUserIdRef.current = userId;
  }, [user, qc]);

  return null;
}

function AppRouter() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Switch>
        <Route path="/" component={HomeRedirect} />
        <Route path="/sign-in" component={() => <Suspense fallback={<PageLoader />}><SignInPage /></Suspense>} />
        <Route path="/sign-up" component={() => <Suspense fallback={<PageLoader />}><SignUpPage /></Suspense>} />
        <Route path="/dashboard"   component={() => <ProtectedRoute component={Dashboard} />} />
        <Route path="/files"       component={() => <ProtectedRoute component={MyDrive} />} />
        <Route path="/files/:id"   component={() => <ProtectedRoute component={MyDrive} />} />
        <Route path="/starred"     component={() => <ProtectedRoute component={Starred} />} />
        <Route path="/recent"      component={() => <ProtectedRoute component={Recent} />} />
        <Route path="/trash"       component={() => <ProtectedRoute component={Trash} />} />
        <Route path="/search"      component={() => <ProtectedRoute component={SearchResults} />} />
        <Route path="/plans"       component={() => <ProtectedRoute component={PlansPage} />} />
        <Route path="/settings"    component={() => <ProtectedRoute component={SettingsPage} />} />
        <Route path="/help"        component={() => <ProtectedRoute component={HelpPage} />} />
        <Route path="/migration"   component={() => <Redirect to="/files" />} />
        <Route path="/duplicates"  component={() => <ProtectedRoute component={DuplicatesPage} />} />
        <Route path="/gift"        component={() => <ProtectedRoute component={GiftStorage} />} />
        <Route path="/manifest"    component={() => <Suspense fallback={<PageLoader />}><Manifest /></Suspense>} />
        <Route path="/privacy"     component={() => <Suspense fallback={<PageLoader />}><Privacy /></Suspense>} />
        <Route path="/terms"       component={() => <Suspense fallback={<PageLoader />}><Terms /></Suspense>} />
        <Route path="/offer"       component={() => <Suspense fallback={<PageLoader />}><Offer /></Suspense>} />
        <Route path="/refund"      component={() => <Suspense fallback={<PageLoader />}><Refund /></Suspense>} />
        <Route path="/releases"    component={() => <Suspense fallback={<PageLoader />}><Releases /></Suspense>} />
        <Route path="/faq"         component={() => <Suspense fallback={<PageLoader />}><FaqPage /></Suspense>} />
        <Route path="/dev"         component={() => <Suspense fallback={<PageLoader />}><DevPortal /></Suspense>} />
        <Route path="/developers"  component={() => <Redirect to="/dev" />} />
        <Route path="/payment/success" component={() => <Suspense fallback={<PageLoader />}><PaymentSuccess /></Suspense>} />
        <Route path="/payment/fail"    component={() => <Suspense fallback={<PageLoader />}><PaymentFail /></Suspense>} />
        <Route path="/s/:token" component={() => <Suspense fallback={<PageLoader />}><SharedFile /></Suspense>} />
        <Route path="/share/:token" component={() => {
          const loc = window.location.pathname;
          const t = loc.split("/share/")[1];
          if (t) window.location.replace(`/s/${t}`);
          return null;
        }} />
        <Route path="/oauth/authorize" component={() => <Suspense fallback={<PageLoader />}><OAuthAuthorize /></Suspense>} />
        <Route path="/admin/login" component={() => <Suspense fallback={<PageLoader />}><AdminLogin /></Suspense>} />
        <Route path="/admin"       component={() => <Suspense fallback={<PageLoader />}><AdminPanel /></Suspense>} />
        <Route component={() => <Suspense fallback={<PageLoader />}><NotFound /></Suspense>} />
      </Switch>
    </Suspense>
  );
}

function App() {
  return (
    <WouterRouter base={basePath}>
      <AuthProvider>
        <QueryClientProvider client={queryClient}>
          <TokenBridge />
          <UploadProvider>
            <TooltipProvider>
              <AppRouter />
              <CookieConsent />
              <Toaster />
            </TooltipProvider>
          </UploadProvider>
        </QueryClientProvider>
      </AuthProvider>
    </WouterRouter>
  );
}

export default App;
